import { firestore, tripsCollection } from "../firebase"
import { doc, getDoc, setDoc, updateDoc, writeBatch } from "firebase/firestore"
import { IAgentProfile, ITripCollaborator, IUser, UserPayload } from "@cruisen/contract"
import { upsertAgentProfile } from "../firebase-functions/agentProfile"
import { getUserTrips } from "./trips"
import { getTripRooms } from "./rooms"

export const createUser = async (
  newUserData: UserPayload,
  agentProfile?: Partial<IAgentProfile>,
) => {
  console.log("create user", { userId: newUserData.id })

  const newUser = {
    ...newUserData,
    platform: "web",
    created: Date.now(),
    updated: Date.now(),
  }

  try {
    const userDocRef = doc(firestore, "Users", newUser.id)
    await setDoc(userDocRef, newUser)
    console.info("User has been created", newUser)

    if (agentProfile) {
      await upsertAgentProfile({
        agentProfile: {
          userId: newUser.id,
          ...agentProfile,
        },
      })
    }

    return newUser
  } catch (error) {
    console.error("Error creating user:", error)
    throw error
  }
}

export const updateUser = async (userId: string, updatedUserData: Partial<UserPayload>) => {
  console.log("update user", { userId })

  const userDocRef = doc(firestore, "Users", userId)
  const userDoc = await getDoc(userDocRef)

  if (!userDoc.exists()) {
    console.warn("User not found", { userId })
    return { success: false, user: null }
  }

  await updateDoc(userDocRef, {
    ...updatedUserData,
    updated: Date.now(),
  })
  console.info("User has been updated", { userId, ...updatedUserData })

  const updatedUserDoc = await getDoc(userDocRef)
  const updatedUser = { ...updatedUserDoc.data(), id: updatedUserDoc.id } as IUser

  return { success: true, user: updatedUser }
}

export const fetchUserByUid = async (uid: string): Promise<IUser | null> => {
  try {
    console.log("fetch user", { userId: uid })
    const userDocRef = doc(firestore, "Users", uid)
    const userDoc = await getDoc(userDocRef)

    if (!userDoc.exists()) {
      console.log(`User with UID ${uid} not found`)
      return null
    }

    const user = { ...userDoc.data(), id: userDoc.id } as IUser
    console.info(`User found: ${user}: ${JSON.stringify(user)}`)
    return user
  } catch (error) {
    console.error("Error fetching user:", error)
    return null
  }
}

export const updateUserDataInTrips = async ({
  userId,
  updatedData,
}: {
  userId: string
  updatedData: Pick<ITripCollaborator, "firstName" | "lastName" | "avatar">
}) => {
  try {
    const trips = await getUserTrips(userId)
    const batch = writeBatch(firestore)

    for (const trip of trips) {
      const tripDocRef = doc(tripsCollection, trip.id)
      const collaborator = trip.collaborators.find((item) => item.userId === userId)
      if (!collaborator) continue

      const collaboratorDiff = Object.entries(updatedData).reduce(
        (acc, [key, value]) => {
          if (collaborator[key] !== value) {
            acc[key] = value
          }
          return acc
        },
        {} as typeof updatedData,
      )

      if (!Object.keys(collaboratorDiff).length) continue

      const updatedCollaborators = trip.collaborators.map((collaborator) =>
        collaborator.userId === userId ? { ...collaborator, ...updatedData } : collaborator,
      )

      batch.update(tripDocRef, {
        collaborators: updatedCollaborators,
        updated: Date.now(),
      })

      const tripRooms = await getTripRooms(trip.id)
      const roomWithUser = tripRooms.find((tripRoom) =>
        tripRoom.guests.some((guest) => guest.userId === userId),
      )

      if (!roomWithUser) continue

      const updatedGuests = roomWithUser.guests.map((guest) =>
        guest.userId === userId ? { ...guest, ...updatedData } : guest,
      )

      /** @TODO: check if this is correct */
      const roomDocRef = doc(tripsCollection, trip.id, "Rooms", roomWithUser.id)
      // const roomDocRef = tripDocRef.collection("Rooms").doc(roomWithUser.id)
      batch.update(roomDocRef, { guests: updatedGuests })
    }

    await batch.commit()
  } catch (error) {
    console.error("Failed to update user data in trips:", error)
  }
}
